import debounce from 'lodash/debounce';
import { fireInitializationEvent } from 'view/forge-ui-analytics/common';
import {
    INITIALIZATION_ACTIONS,
    FORGE_SAMPLING_RATE,
    DEBOUNCE_INTERVAL,
} from 'view/forge-ui-analytics/common/constants';
import { Math } from '@atlassian/help-center-common-util/math';
import type { AnalyticsAttributes, Source } from 'view/forge-ui-analytics/common/types';

// Shared debounced functions per source and action type are used to ensure that multiple components
// with the same source and action type will share the same debounced function. This prevents duplicate
// analytics events when multiple components finish or fail initialization within the debounce interval.
const sharedDebouncedEvents = new Map<string, ReturnType<typeof debounce>>();

const getCompositeKey = (
    source: Source,
    action: (typeof INITIALIZATION_ACTIONS)[keyof typeof INITIALIZATION_ACTIONS]
) => `${source}-${action}`;

const getOrCreateDebouncedFn = (key: string, fn: (attributes?: AnalyticsAttributes) => Promise<void>) => {
    const existingFn = sharedDebouncedEvents.get(key);
    if (existingFn) {
        return existingFn;
    }
    const debouncedFn = debounce(fn, DEBOUNCE_INTERVAL);
    sharedDebouncedEvents.set(key, debouncedFn);
    return debouncedFn;
};

const isInSample = (samplingRate: number): boolean => Math.random() * 100 < samplingRate;

export const createInitializationFinished = (source: Source, shouldDebounce = false) => {
    const fireEvent = async (attributes?: AnalyticsAttributes) => {
        if (isInSample(FORGE_SAMPLING_RATE)) {
            await fireInitializationEvent(INITIALIZATION_ACTIONS.FINISHED, {
                source,
                attributes,
                id: 'fetchModulesInitialization',
            });
        }
    };
    if (shouldDebounce) {
        return getOrCreateDebouncedFn(getCompositeKey(source, INITIALIZATION_ACTIONS.FINISHED), fireEvent);
    }
    return fireEvent;
};

export const createInitializationFailed = (source: Source, shouldDebounce = false) => {
    const fireEvent = async (attributes?: AnalyticsAttributes) => {
        await fireInitializationEvent(INITIALIZATION_ACTIONS.FAILED, {
            source,
            attributes,
            id: 'fetchModulesInitialization',
        });
    };
    if (shouldDebounce) {
        return getOrCreateDebouncedFn(getCompositeKey(source, INITIALIZATION_ACTIONS.FAILED), fireEvent);
    }
    return fireEvent;
};
